import {Fragment, useState} from 'react';
import {Link} from 'react-router-dom';
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronDown,
  ChevronRight,
  Download,
  Loader2,
  Search,
} from 'lucide-react';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@common-web/components/ui/table';
import {Alert, AlertDescription} from '@common-web/components/ui/alert';
import {Button} from '@common-web/components/ui/button';
import {Input} from '@common-web/components/ui/input';
import {Checkbox} from '@common-web/components/ui/checkbox';
import {Switch} from '@common-web/components/ui/switch';
import {Label} from '@common-web/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {TableHeaderCount} from '@common-web/components/table-header-count';
import {ListPaginationFooter} from '@common-web/components/list-pagination-footer';
import {toast} from '@common-web/hooks/use-toast';
import {downloadCsvFile, makeCsvFilename} from '@common-web/lib/downloadCsv';
import {cn} from '@common-web/lib/utils';
import type {Column, DataTableProps} from './types';
import {buildCsvRows, hasExportableColumns, rowsToCsvString} from './csv';

const ALIGN_CLASS: Record<NonNullable<Column<unknown>['align']>, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

function SortIcon({state}: {state: 'asc' | 'desc' | 'none'}) {
  if (state === 'asc') return <ArrowUp className='h-3.5 w-3.5 ml-1 inline-block' />;
  if (state === 'desc') return <ArrowDown className='h-3.5 w-3.5 ml-1 inline-block' />;
  return <ArrowUpDown className='h-3.5 w-3.5 ml-1 inline-block opacity-30' />;
}

export function DataTable<T, TFilters extends Record<string, unknown> = {}>({
  columns,
  data,
  rowKey,
  table,
  total,
  totalPages,
  loading = false,
  error = null,
  emptyMessage = 'No data available',
  title,
  entity,
  searchPlaceholder,
  filters,
  showClearFilters,
  csvExport,
  selectable,
  bulkActions,
  onRowClick,
  rowHref,
  rowClassName,
  renderExpandedContent,
  getRowCanExpand,
}: DataTableProps<T, TFilters>) {
  const showCard = Boolean(title);
  const totalForCount = total ?? data?.length ?? 0;

  const [csvLoading, setCsvLoading] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  const showSearch = Boolean(table && searchPlaceholder);
  const showFilters = Boolean(table && filters && filters.length > 0);
  const showCsv = Boolean(csvExport && hasExportableColumns(columns));
  const hasActiveFilters = Boolean(
    table &&
      (table.search !== '' ||
        Object.values(table.filterValues).some(
          (value) => value !== '' && value !== false && value != null,
        )),
  );
  const showClearLink = Boolean(showClearFilters && table && hasActiveFilters);
  const showToolbar = showSearch || showFilters || showCsv;

  if (selectable && !rowKey) {
    throw new Error('DataTable: `rowKey` is required when `selectable` is true.');
  }
  if (selectable && !table) {
    throw new Error(
      'DataTable: `table` (from useDataTable) is required when `selectable` is true.',
    );
  }

  const canExpand = Boolean(renderExpandedContent);
  if (canExpand && !rowKey) {
    throw new Error('DataTable: `rowKey` is required when `renderExpandedContent` is set.');
  }

  const canSelect = Boolean(selectable && table && rowKey);
  const currentPageKeys = canSelect && data ? data.map((item) => rowKey!(item)) : [];
  const allSelected = canSelect && table!.isAllSelected(currentPageKeys);
  const showBulkBar = canSelect && (table?.selection.size ?? 0) > 0;

  const colSpanCount = columns.length + (canSelect ? 1 : 0) + (canExpand ? 1 : 0);

  const toggleExpanded = (key: string) =>
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const handleCsvExport = async () => {
    if (!csvExport) return;
    setCsvLoading(true);
    try {
      const rows = 'getAllRows' in csvExport ? await csvExport.getAllRows() : (data ?? []);
      const csvRows = buildCsvRows(rows, columns);
      const csv = rowsToCsvString(csvRows);
      downloadCsvFile(csv, makeCsvFilename(csvExport.filename));
    } catch (err) {
      toast({
        title: 'Export failed',
        description: err instanceof Error ? err.message : 'Unable to export CSV.',
        variant: 'destructive',
      });
    } finally {
      setCsvLoading(false);
    }
  };

  const toolbar = showToolbar ? (
    <div className='flex flex-wrap items-center gap-3 px-6 py-4 border-b border-gray-100 bg-white'>
      {showSearch && (
        <div className='relative flex-1 min-w-[200px] max-w-md'>
          <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
          <Input
            value={table!.search}
            onChange={(e) => table!.setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            className='pl-9'
          />
        </div>
      )}
      {showFilters &&
        filters!.map((f) => {
          if (f.type === 'switch') {
            return (
              <div key={f.key} className='flex items-center gap-2'>
                <Switch
                  id={`filter-${f.key}`}
                  checked={Boolean(table!.filterValues[f.key])}
                  onCheckedChange={(checked) =>
                    table!.setFilter(f.key, checked as TFilters[typeof f.key])
                  }
                />
                <Label htmlFor={`filter-${f.key}`} className='text-sm font-medium'>
                  {f.label}
                </Label>
              </div>
            );
          }
          const currentValue = String(table!.filterValues[f.key] ?? '');
          return (
            <div key={f.key} className='min-w-[160px]'>
              <Select
                value={currentValue || '__all__'}
                onValueChange={(v) =>
                  table!.setFilter(f.key, (v === '__all__' ? '' : v) as TFilters[typeof f.key])
                }
              >
                {/* The trigger carries its own label rather than relying on
                    `SelectValue`, so the control still says what it filters once a
                    value is chosen. The alternative — naming the clear option
                    "All <label>" so the box reads sensibly when nothing is selected
                    — produced phrases like "All Administered by". */}
                <SelectTrigger aria-label={f.label}>
                  <span className='truncate'>
                    <span className='text-muted-foreground'>{f.label}:</span>{' '}
                    {f.options.find((opt) => opt.value === currentValue)?.label ?? 'All'}
                  </span>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='__all__'>All</SelectItem>
                  {f.options.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          );
        })}
      {showClearLink && (
        <Button
          variant='link'
          size='sm'
          className='px-0 text-brand-blue'
          onClick={() => {
            table!.setSearch('');
            table!.resetFilters();
          }}
        >
          Clear filters
        </Button>
      )}
      {showCsv && (
        <div className='ml-auto'>
          <Button
            variant='outline'
            size='sm'
            onClick={handleCsvExport}
            disabled={csvLoading || !data || data.length === 0}
          >
            {csvLoading ? (
              <Loader2 className='h-4 w-4 mr-2 animate-spin' />
            ) : (
              <Download className='h-4 w-4 mr-2' />
            )}
            CSV
          </Button>
        </div>
      )}
    </div>
  ) : null;

  const tableBody = (
    <div className='w-full max-w-full overflow-x-auto'>
      <div className='rounded-lg border max-w-full border-neutral-200 bg-white shadow-sm'>
        <Table className='whitespace-nowrap'>
          <TableHeader>
            <TableRow className='bg-neutral-50 border-b border-neutral-200 hover:bg-neutral-50'>
              {canSelect && (
                <TableHead className='w-[44px]'>
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={() => table!.toggleAll(currentPageKeys)}
                    aria-label='Select all rows on this page'
                  />
                </TableHead>
              )}
              {canExpand && <TableHead className='w-[40px]' />}
              {columns.map((col) => {
                const sortState =
                  table && col.sortable && table.sort?.key === (col.sortKey ?? col.id)
                    ? table.sort.direction
                    : 'none';
                const isSortable = Boolean(table && col.sortable);
                return (
                  <TableHead
                    key={col.id}
                    className={cn(
                      'font-semibold text-neutral-700',
                      col.align && ALIGN_CLASS[col.align],
                      col.width,
                      col.headerClassName,
                      isSortable && 'cursor-pointer select-none hover:bg-neutral-100',
                    )}
                    onClick={
                      isSortable ? () => table!.toggleSort(col.sortKey ?? col.id) : undefined
                    }
                  >
                    <span className='inline-flex items-center'>
                      {col.header}
                      {isSortable && <SortIcon state={sortState} />}
                    </span>
                  </TableHead>
                );
              })}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={colSpanCount} className='text-center py-12'>
                  <div className='flex items-center justify-center gap-2 text-muted-foreground'>
                    <Loader2 className='h-5 w-5 animate-spin' />
                    Loading...
                  </div>
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={colSpanCount} className='py-6'>
                  <Alert variant='destructive' className='border-red-200 bg-red-50'>
                    <AlertCircle className='h-4 w-4' />
                    <AlertDescription>{error.message}</AlertDescription>
                  </Alert>
                </TableCell>
              </TableRow>
            ) : !data || data.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={colSpanCount}
                  className='text-center py-12 text-muted-foreground'
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              data.map((item, idx) => {
                const key = rowKey ? rowKey(item) : String(idx);
                const href = rowHref?.(item);
                const rowExpandable = canExpand && (getRowCanExpand ? getRowCanExpand(item) : true);
                const isExpanded = rowExpandable && expandedKeys.has(key);
                const interactive = Boolean(href || onRowClick || rowExpandable);
                const handleRowClick = rowExpandable
                  ? () => toggleExpanded(key)
                  : onRowClick
                    ? () => onRowClick(item)
                    : undefined;
                return (
                  <Fragment key={key}>
                    <TableRow
                      className={cn(
                        'border-b border-neutral-100 transition-colors duration-150',
                        idx % 2 === 1 && 'bg-neutral-50/30',
                        interactive && 'hover:bg-brand-50/30 hover:shadow-sm cursor-pointer',
                        rowClassName?.(item),
                      )}
                      onClick={handleRowClick}
                    >
                      {canSelect && (
                        <TableCell className='w-[44px]'>
                          <Checkbox
                            checked={table!.selection.has(key)}
                            onCheckedChange={() => table!.toggleRow(key)}
                            onClick={(e) => e.stopPropagation()}
                            aria-label='Select row'
                          />
                        </TableCell>
                      )}
                      {canExpand && (
                        <TableCell className='w-[40px] text-neutral-500'>
                          {rowExpandable &&
                            (isExpanded ? (
                              <ChevronDown className='h-4 w-4' />
                            ) : (
                              <ChevronRight className='h-4 w-4' />
                            ))}
                        </TableCell>
                      )}
                      {columns.map((col) => {
                        const cell = col.render(item);
                        return (
                          <TableCell
                            key={col.id}
                            className={cn(col.align && ALIGN_CLASS[col.align], col.className)}
                          >
                            {href ? (
                              <Link to={href} className='block'>
                                {cell}
                              </Link>
                            ) : (
                              cell
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                    {isExpanded && (
                      <TableRow className='bg-neutral-50/50 hover:bg-neutral-50/50'>
                        <TableCell colSpan={colSpanCount} className='p-0'>
                          {renderExpandedContent!(item)}
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );

  if (!showCard) return tableBody;

  return (
    <Card className='border-0 shadow-lg bg-white overflow-hidden'>
      <CardHeader className='bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 px-6 py-4'>
        <div className='flex items-center justify-between'>
          <CardTitle className='text-lg font-semibold text-brand-navy'>{title}</CardTitle>
          {entity && (
            <TableHeaderCount
              count={totalForCount}
              singular={entity.singular}
              plural={entity.plural}
            />
          )}
        </div>
      </CardHeader>
      <CardContent className='p-0'>
        {toolbar}
        {showBulkBar && bulkActions && bulkActions.length > 0 && (
          <div className='flex items-center gap-3 px-6 py-3 bg-brand-50 border-b border-brand-100'>
            <span className='text-sm font-medium text-brand-navy'>
              {table!.selection.size} selected
            </span>
            <div className='flex items-center gap-2 ml-auto'>
              {bulkActions.map((action) => {
                const Icon = action.icon;
                return (
                  <Button
                    key={action.label}
                    variant='outline'
                    size='sm'
                    onClick={() => action.onClick(table!.selection)}
                  >
                    {Icon && <Icon className='h-4 w-4 mr-2' />}
                    {action.label}
                  </Button>
                );
              })}
              <Button variant='ghost' size='sm' onClick={() => table!.clearSelection()}>
                Clear
              </Button>
            </div>
          </div>
        )}
        <div className='p-6'>{tableBody}</div>
        {table && totalPages !== undefined && total !== undefined && totalPages > 1 && (
          <ListPaginationFooter
            page={table.page}
            pageSize={table.pageSize}
            total={total}
            totalPages={totalPages}
            onPageChange={table.setPage}
            onPageSizeChange={table.setPageSize}
            rangeEntityLabel={entity?.plural}
          />
        )}
      </CardContent>
    </Card>
  );
}
